import { type KeyboardEvent, type MouseEvent, useState } from 'react';
import { comboboxKey } from '@/utils/comboboxKey';

type Options<T> = {
  /** The listbox's id; option ids derive from it. */
  id: string;
  /** The suggestions shown now (empty: the list is closed). */
  items: T[];
  onPick: (item: T) => void;
  /** Escape closes the list; without it Escape is left alone (it may close a sheet). */
  onClose?: () => void;
  /** Arrow down opens a list that was closed (Escape, or not yet typed in). */
  onOpen?: () => void;
};

/**
 * A text field with suggestions below it, as an ARIA combobox: the arrows
 * highlight a suggestion, Enter picks it, Escape closes the list. The focus
 * stays in the field (aria-activedescendant), also when an option is clicked.
 * Spread `input` on the field, `list` on the element with role="listbox" (written
 * out, so lint sees the role) and `option(i, item)` on each option.
 */
export function useCombobox<T>({ id, items, onPick, onClose, onOpen }: Options<T>) {
  const [highlight, setHighlight] = useState(-1);
  // A refetched list can be shorter: an index past its end means none.
  const active = highlight < items.length ? highlight : -1;
  const optionId = (i: number) => `${id}-${i}`;

  // After a pick nothing stays highlighted: the next Enter must not pick
  // whatever moved into that place (or submit a choice nobody made).
  const pick = (item: T) => {
    setHighlight(-1);
    onPick(item);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // A closed list: arrow down opens it with the first suggestion highlighted.
    if (e.key === 'ArrowDown' && !items.length && onOpen) {
      e.preventDefault();
      onOpen();
      setHighlight(0);
      return;
    }
    const action = comboboxKey(e.key, active, items.length, onClose !== undefined);
    if (!action) return;
    e.preventDefault();
    if (action === 'close') {
      // Only the list: not a sheet around the field.
      e.stopPropagation();
      setHighlight(-1);
      onClose?.();
    } else if (action === 'pick') {
      const item = items[active];
      if (item !== undefined) pick(item);
    } else {
      setHighlight(action.move);
    }
  };

  return {
    active,
    /** Typing again: nothing highlighted. */
    reset: () => setHighlight(-1),
    input: {
      role: 'combobox' as const,
      'aria-expanded': items.length > 0,
      'aria-controls': id,
      'aria-autocomplete': 'list' as const,
      'aria-activedescendant': active >= 0 ? optionId(active) : undefined,
      onKeyDown,
    },
    list: { id },
    option: (i: number, item: T) => ({
      id: optionId(i),
      role: 'option' as const,
      tabIndex: -1,
      'aria-selected': i === active,
      // Keep the focus in the field: a click must not blur it first.
      onMouseDown: (e: MouseEvent) => e.preventDefault(),
      onPointerMove: () => setHighlight(i),
      onClick: () => pick(item),
    }),
  };
}
